import { useState } from 'react'

import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { JobCard } from '@/components/ui/JobCard'
import { Pagination } from '@/components/ui/Pagination'
import type { StudentJobFilters } from '@/features/jobs/api/jobs.api'
import { useStudentJobs } from '@/features/jobs/hooks/useJobs'
import { humanizeEnum } from '@/utils/format'

const EMPLOYMENT_TYPE_OPTIONS = ['full_time', 'part_time', 'internship', 'contract', 'freelance']
const WORK_MODE_OPTIONS = ['onsite', 'remote', 'hybrid']
const PER_PAGE = 12

export function JobsListPage() {
  const [filters, setFilters] = useState<StudentJobFilters>({ page: 1, per_page: PER_PAGE })
  const { data, isPending, isError } = useStudentJobs(filters)

  const updateFilter = (patch: Partial<StudentJobFilters>) =>
    setFilters((f) => ({ ...f, ...patch, page: 1 }))

  return (
    <>
      <div className="my-4 page-header-breadcrumb">
        <p className="fw-semibold fs-20 mb-0">Browse Jobs</p>
      </div>

      <div className="card custom-card mb-3">
        <div className="card-body row g-2">
          <div className="col-md-5">
            <input
              type="search"
              className="form-control"
              placeholder="Search by title or keyword"
              value={filters.q ?? ''}
              onChange={(e) => updateFilter({ q: e.target.value || undefined })}
            />
          </div>
          <div className="col-md-3">
            <input
              type="text"
              className="form-control"
              placeholder="City"
              value={filters.location_city ?? ''}
              onChange={(e) => updateFilter({ location_city: e.target.value || undefined })}
            />
          </div>
          <div className="col-md-2">
            <select
              className="form-select"
              value={filters.employment_type ?? ''}
              onChange={(e) => updateFilter({ employment_type: e.target.value || undefined })}
            >
              <option value="">All types</option>
              {EMPLOYMENT_TYPE_OPTIONS.map((v) => (
                <option key={v} value={v}>
                  {humanizeEnum(v)}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <select
              className="form-select"
              value={filters.work_mode ?? ''}
              onChange={(e) => updateFilter({ work_mode: e.target.value || undefined })}
            >
              <option value="">All modes</option>
              {WORK_MODE_OPTIONS.map((v) => (
                <option key={v} value={v}>
                  {humanizeEnum(v)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {isPending && <p className="text-muted">Loading jobs…</p>}
      {isError && (
        <EmptyState
          heading="Couldn't load jobs"
          message="Please try again."
          action={
            <Button variant="ghost" onClick={() => setFilters((f) => ({ ...f }))}>
              Retry
            </Button>
          }
        />
      )}

      {data && data.jobs.length === 0 && (
        <EmptyState heading="No jobs found" message="Try adjusting your search or filters." />
      )}

      {data && data.jobs.length > 0 && (
        <>
          <div className="row g-3">
            {data.jobs.map((job) => (
              <div className="col-md-6 col-xl-4" key={job.id}>
                <JobCard
                  title={job.title}
                  organizationName={job.organizationName}
                  organizationLogoUrl={job.organizationLogoUrl}
                  locationCity={job.locationCity}
                  locationState={job.locationState}
                  workMode={job.workMode}
                  employmentType={job.employmentType}
                  experienceMinMonths={job.experienceMinMonths}
                  experienceMaxMonths={job.experienceMaxMonths}
                  salaryMin={job.salaryMin}
                  salaryMax={job.salaryMax}
                  currency={job.currency}
                  publishedAt={job.publishedAt}
                  detailHref={`/jobs/${job.id}`}
                />
              </div>
            ))}
          </div>
          <div className="mt-3">
            <Pagination
              meta={data.meta}
              onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
            />
          </div>
        </>
      )}
    </>
  )
}
